import { of, forkJoin, throwError } from 'rxjs'
import { mergeMap, switchMap } from 'rxjs/operators'
import VoltError from 'VoltError'
import { serialize } from 'helpers/index.js'
import DataHelper from 'framework/helpers/data'
import Constants from 'api-constants'
import Fetch from '../fetch'
import {
    ProductFactory,
    mergeProducts,
    UserEntitlementFactory,
    updateProductsEntitlements,
} from '../factories'
import { UserEntitlement, NagraProduct } from '../models'
import ConfigHelper from 'framework/helpers/config'
import CommonApi from 'services/common'

/**
 * Deal with Purchase (data retrieval, purchase and subscription actions)
 */
export default class PurchaseApi extends Fetch {
    constructor(config, otherApis = {}) {
        super(config, otherApis)
        this.metaApi = otherApis.metaApi
    }

    /**
     * Retrieves all Subscription products
     *
     * @param {Number} [limit=50] The page size of each api request
     * @returns {Observable<SubscriptionsList>}
     */
    getSubscriptions(limit = 50) {
        return this._getSubscriptions().pipe(
            switchMap((products) => of({ isComplete: true, products }))
        )
    }

    /**
     * Retrieves all Subscription products
     *
     * @param {Number} [limit=50] The page size of each api request
     * @returns {Observable<SubscriptionsList>}
     */
    getSubscriptionsData(productIds) {
        return this._getSubscriptions(productIds)
    }

    /**
     * Retrieves TVOD products
     * @param {Object} args
     * @param {Number} args.limit The maximum number of products retrieved per request
     * @param {Number} args.page The page number **Important:** 0-based value, first page is `0`
     *
     * @returns {Observable<TVODProductsList>}
     */
    getPurchasedTVODProducts({ limit, page }) {
        return this._getUserEntitlements(Constants.productTypes.TRANSACTIONAL).pipe(
            mergeMap((userEntitlements) => {
                const entitledProductIds = userEntitlements.map(
                    (entitlement) => entitlement.productId
                )
                if (!entitledProductIds.length) {
                    return of([])
                }
                return this._getProducts(
                    Constants.productTypes.TRANSACTIONAL,
                    entitledProductIds
                ).pipe(
                    mergeMap((nagraTVODProducts) => {
                        return of({
                            isComplete: true,
                            products: updateProductsEntitlements(
                                nagraTVODProducts,
                                userEntitlements,
                                CommonApi.getInstance().getPaymentMethodsTypes()
                            ),
                        })
                    })
                )
            })
        )
    }

    /**
     * Retrieves Subscribed products (PACKS)
     *
     * @returns {Observable<Subscription>}
     */
    getPurchasedSubscriptions() {
        return this._getUserEntitlements(Constants.productTypes.SUBSCRIPTION).pipe(
            mergeMap((userEntitlements) => {
                const entitledProductIds = userEntitlements.map(
                    (entitlement) => entitlement.productId
                )
                if (!entitledProductIds.length) {
                    return of([])
                }
                return this._getProducts(
                    Constants.productTypes.SUBSCRIPTION,
                    entitledProductIds
                ).pipe(
                    mergeMap((nagraSubscriptions) => {
                        return of({
                            isComplete: true,
                            products: updateProductsEntitlements(
                                nagraSubscriptions,
                                userEntitlements,
                                CommonApi.getInstance().getPaymentMethodsTypes()
                            ),
                        })
                    })
                )
            })
        )
    }

    /**
     * Retrieves all purchased TVOD products
     *
     * @param {Number} [pageSize=15] The page size of each api request
     *
     * @returns {Observable<TVODProductsList>}
     */
    getAllPurchasedTVODProducts(limit = 15) {
        return this.getPurchasedTVODProducts({ limit })
    }

    getTVODProductsData(productIds) {
        return this._getProducts(Constants.productTypes.TRANSACTIONAL, productIds).pipe(
            mergeMap((nagraTVODProducts) => {
                return this._getUserEntitlements(
                    Constants.productTypes.TRANSACTIONAL,
                    productIds
                ).pipe(
                    mergeMap((userEntitlements) => {
                        return of(
                            updateProductsEntitlements(
                                nagraTVODProducts,
                                userEntitlements,
                                CommonApi.getInstance().getPaymentMethodsTypes()
                            )
                        )
                    })
                )
            })
        )
    }

    /**
     * From the titleId, retrieve property of related Content (ex: isHD)
     * and the associated product identifiers.
     *
     * @param {Object} args
     * @param {String} args.id Title identifier
     * @returns {Observable<Array<TVODProduct|Subscription>>} A list of {@link TVODProduct} or {@link Subscription} products
     */
    getProductsFromTitle({ subscriptionIds, isTvod, id: titleId, streams }) {
        if (!subscriptionIds.length) {
            return of([])
        }
        return (
            isTvod
                ? this.getTVODProductsData(subscriptionIds)
                : this.getSubscriptionsData(subscriptionIds)
        ).pipe(
            mergeMap((result) => {
                const updatedResult = result.map((s) => {
                    const contents = (streams || []).reduce((acc, stream) => {
                        if (stream && stream.uri) {
                            acc[stream.uri] = {
                                id: stream.uri,
                                isHD: s.titles && s.titles[titleId] && s.titles[titleId].isHD,
                            }
                        }
                        return acc
                    }, {})

                    return s.update({
                        titles: {
                            [titleId]: {
                                id: titleId,
                                contents: {
                                    ...contents,
                                },
                            },
                        },
                    })
                })

                return of(updatedResult)
            })
        )
    }

    // ---------------------------- NESTED METHODS ----------------------------
    /**
     * Retrieves Products
     * @param {'btv'|'vod'} serviceType
     * @param {'subscription'|'transactional'} productType
     * @param {Number} limit Page size
     * @param {Array<String>|<String>} productIds
     * @returns {Observable<Array<BTVProduct>>}
     */
    _getNagraProducts(
        serviceType = NagraProduct.SERVICE_TYPE.VOD,
        productType,
        productIds,
        limit = 200
    ) {
        const { metadataProvider = 'GLOBAL' } = ConfigHelper.getInstance().getConfig('nagra')
        const offset = 0
        let filters = []
        if (productIds && productIds.length > 0) {
            filters.push(this._appendFilterList('$or', 'id', productIds))
        }
        productType = NagraProduct.getProductType(productType)
        if (productType) {
            filters.push(this._appendFilter('type', productType))
        }
        const filter = this._buildFilter(filters)

        return this.recursiveFetch(
            (_, limit, offset) => {
                return this.fetch({
                    url: `${
                        this.config.urls.apiUrl
                    }/metadata/delivery/${metadataProvider}/${serviceType}/products${serialize({
                        token: DataHelper.getInstance().getSecondaryAccessToken(),
                        offset: offset,
                        limit: limit,
                        filter,
                    })}`,
                    method: 'GET',
                    log: `[GET ${serviceType} PRODUCTS][${productType}][OFFSET=${offset}][LIMIT=${limit}]`,
                })
            },
            {
                limit,
                offset,
                responseResource: 'products',
            }
        ).pipe(
            mergeMap((products) => {
                const result = (products || []).map((x) => ProductFactory(x))
                return of(result)
            })
        )
    }

    /**
     * Retrieves User entitlements
     * @param {SUBSCRIPTION|TRANSACTIONAL|FREE|CAPABILITY} productType @link UserEntitlement.PRODUCT_TYPE
     * @param {Number} limit Page size
     * @param {Array<String>|<String>} productIds
     * @returns {Observable<Array<UserEntitlement>>}
     */
    _getUserEntitlements(productType, productIds, limit = 50) {
        let filters = []
        productType = UserEntitlement.getProductType(productType)

        if (productIds && productIds.length > 0) {
            filters.push(this._appendFilterList('$or', 'productId', productIds))
        } else if (productType) {
            filters.push(this._appendFilter('productType', productType))
        }
        /**
         * When 'productType' is specified from filter parameter, for unknown reason on Nagra backend
         * It returns all SUBSCRIPTIONS including duplicated/previous inactive one for the same product
         * Leading to error from the internal algorithm when trying to resolve the entitlement of a package
         *
         * Ex: 4 Entitlements returned for the same product when 'productType' is specified
         * - GLOBAL_5111009512 - "UNSUBSCRIBED"
         * - GLOBAL_5111009512 - "UNSUBSCRIBED"
         * - GLOBAL_5111009512 - "UNSUBSCRIBED"
         * - GLOBAL_5111009512 - "SUBSCRIBED"
         *
         * So add filtering by a "status" to retrieve only active entitltements
         */
        filters.push(this._appendFilter('status', UserEntitlement.STATUS.SUBSCRIBED))

        const filter = this._buildFilter(filters)

        const offset = 0
        return this.recursiveFetch(
            (_, limit, offset) => {
                return this.fetch({
                    url: `${this.config.urls.apiUrl}/rmg/v1/user/entitlements${serialize({
                        page: offset / limit + 1,
                        limit: limit,
                        filter,
                    })}`,
                    method: 'GET',
                    headers: {
                        'Content-Type': Constants.httpHeader.ContentType.APPLICATION_JSON,
                        'Client-token': DataHelper.getInstance().getSecondaryAccessToken(),
                    },
                    log: `[GET USER ENTITLEMENTS][${productType}][OFFSET=${offset}][LIMIT=${limit}]`,
                })
            },
            {
                pageSize: limit,
                offset,
                responseResource: 'resourceSet',
            }
        ).pipe(
            mergeMap((entitlement) => {
                const result = (entitlement || []).map((x) => UserEntitlementFactory(x))
                return of(result)
            })
        )
    }

    /**
     * Retrieves Products whether from the PROXY or from Nagra
     * @param {'subscription'|'transactional'} productType
     * @param {Number} limit Page size
     * @param {Array<String>|<String>} productIds
     * @returns {Observable<Array<BTVProduct>>}
     */
    _getProducts(productType = Constants.productTypes.SUBSCRIPTION, productIds, limit = 200) {
        const { useProxyProducts = true } = ConfigHelper.getInstance().getConfig('nagra')
        if (useProxyProducts) {
            return productIds
                ? this.metaApi.getProductsById(productType, {
                      ids: productIds,
                  })
                : this.metaApi.getAllProducts(productType, {
                      limit,
                  })
        }

        if (Constants.productTypes.SUBSCRIPTION === productType) {
            return forkJoin([
                // GET Nagra VOD Products
                this._getNagraProducts(NagraProduct.SERVICE_TYPE.VOD, productType, productIds),
                // GET Nagra TV Products
                this._getNagraProducts(NagraProduct.SERVICE_TYPE.BTV, productType, productIds),
            ]).pipe(
                mergeMap(([nagraVODProducts, nagraTVProducts]) => {
                    const products = mergeProducts(nagraVODProducts, nagraTVProducts)
                    return of(products)
                })
            )
        } else {
            return this._getNagraProducts(
                NagraProduct.SERVICE_TYPE.VOD,
                Constants.productTypes.TRANSACTIONAL,
                productIds
            ).pipe(
                mergeMap((nagraTVODProducts) => {
                    return of(nagraTVODProducts)
                })
            )
        }
    }

    /**
     * Retrieves all Subscription products (Using Nagra Products)
     *
     * @param {Number} [limit=50] The page size of each api request
     * @returns {Observable<SubscriptionsList>}
     */
    _getSubscriptions(productIds, limit = 50) {
        return forkJoin([
            // GET Subscriptions
            this._getProducts(Constants.productTypes.SUBSCRIPTION, productIds),
            // THEN user entitlements
            this._getUserEntitlements(Constants.productTypes.SUBSCRIPTION, productIds),
        ]).pipe(
            mergeMap(([products, userEntitlements]) => {
                return of(
                    updateProductsEntitlements(
                        products,
                        userEntitlements,
                        CommonApi.getInstance().getPaymentMethodsTypes()
                    )
                )
            })
        )
    }

    // ---------------------------- TOOLBOX ----------------------------
    /**
     * Build filter list
     * @doc https://docs.nagra.com/doc/otv/otvdoc2051/apis/metadata-server-mds-api-documentation/api-syntax-101
     * @param {Array<String>} filtersList Array of filters to be applied
     * @returns {String} Nagra filter
     */
    _buildFilter(filtersList = []) {
        return filtersList.length > 0 ? `{${filtersList.join(',')}}` : undefined
    }

    // -----------------------------------------------------------------

    // --------------------- FEATURE NOT SUPPORTED ---------------------
    purchaseTVOD(productId, offerId) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[purchaseTVOD] Feature not supported',
            })
        )
    }

    subscribe(productId, offerId) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[subscribe] Feature not supported',
            })
        )
    }

    unsubscribe(productId, offerId, transactionId) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[unsubscribe] Feature not supported',
            })
        )
    }

    activateSubscription({ productId, activationId }) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[activateSubscription] Feature not supported',
            })
        )
    }

    validateAppStorePurchase(productId, offerId, purchaseData, iapReceipt, iapAppstore) {
        return throwError(
            new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE, {
                extraLog: '[validateAppStorePurchase] Feature not supported',
            })
        )
    }

    /**
     * Redeem a voucher code
     *
     * @param {Object} options
     * @param {String} options.code value of voucherCode
     * @returns {Observable<Boolean>} returns boolean
     */
    redeemVoucherCode = ({ code }) => {
        return of(false)
    }

    /**
     * Add payment method for an account
     *
     * @param {Object} options
     * @param {string} options.paymentMethodId the payment method id got from a credit card iframe
     * @returns Observable
     */
    addPaymentMethod = ({ paymentMethodId }) =>
        throwError(new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE))

    /**
     * Delete payment method for an account
     *
     * @param {Object} options
     * @param {string} options.paymentMethodId the payment method id got from a credit card iframe
     * @returns Observable
     */
    deletePaymentMethod = ({ paymentMethodId }) =>
        throwError(new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE))

    sendPurchaseOTP = () => throwError(new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE))
    validatePurchaseOTP = () => throwError(new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE))
    getUserWallet = () => throwError(new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE))
    // ------------------------------------------------------------------------
}
